import { AUTH_STORAGE_KEYS } from "@/constants/authStorageKeys";
import type { WorkosUserProfileLike } from "@/utils/mapWorkosUserProfile";

export type WorkosSessionHints = {
  email?: string;
  userId?: string;
};

function normalizeEmail(email: string | undefined): string | undefined {
  const trimmed = email?.trim().toLowerCase();
  return trimmed || undefined;
}

/**
 * Parse cached workos_user JSON; returns null on missing or invalid payload.
 */
export function parseWorkosUserCache(raw: string | null): WorkosUserProfileLike | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as WorkosUserProfileLike;
    if (!parsed?.id || !parsed?.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Whether a cached profile matches the active session hints from checkAuthStatus / server.
 */
export function isWorkosCacheValidForSession(
  cached: WorkosUserProfileLike,
  session: WorkosSessionHints,
): boolean {
  const sessionEmail = normalizeEmail(session.email);
  const cachedEmail = normalizeEmail(cached.email);

  if (sessionEmail && cachedEmail && sessionEmail !== cachedEmail) {
    return false;
  }

  if (session.userId && cached.id && session.userId !== cached.id) {
    return false;
  }

  return true;
}

export function readWorkosUserCacheFromStorage(): WorkosUserProfileLike | null {
  if (typeof window === "undefined") return null;
  try {
    return parseWorkosUserCache(localStorage.getItem(AUTH_STORAGE_KEYS.WORKOS_USER));
  } catch {
    return null;
  }
}

export function clearWorkosUserCache(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(AUTH_STORAGE_KEYS.WORKOS_USER);
  } catch {
    // ignore
  }
}

/**
 * Load workos_user from localStorage only when it matches session hints.
 */
export function loadValidatedWorkosUserCache(
  session: WorkosSessionHints,
): WorkosUserProfileLike | null {
  const cached = readWorkosUserCacheFromStorage();
  if (!cached) return null;
  if (!isWorkosCacheValidForSession(cached, session)) {
    clearWorkosUserCache();
    return null;
  }
  return cached;
}
